/* eslint-disable camelcase, no-underscore-dangle */

import axios from 'axios';

const getQueryVariable = (variable: string) => {
  try {
    const query = window.location.search.substring(1).split('&');

    for (let i = 0; i < query.length; i++) {
      const pair = query[i].split('=');

      if (decodeURIComponent(pair[0]) === variable)
        return decodeURIComponent(pair[1]);
    }
  } catch (e) {
    //
  }

  return null;
};

const generateGuid = () => 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
  const r = Math.random() * 16 | 0; // eslint-disable-line
  const v = c == 'x' ? r : (r & 0x3 | 0x8); // eslint-disable-line

  return v.toString(16);
});

const getFaid = () => {
  try {
    return window.localStorage.getItem('front.aid');
  } catch (e) {
    return null;
  }
};

const setFaid = () => {
  let aid = getFaid();
  const isAnonymous = aid === null;

  if (isAnonymous) {
    aid = generateGuid();

    try {
      window.localStorage.setItem('front.aid', aid);
    } catch (e) {
      //
    }
  }

  axios.defaults.headers.common['front-anonymous-id'] = aid;

  if (isAnonymous && window.cb_data)
    sendEvent('clearbit-reveal', window.cb_data);
};

const setCookie = (name: string, value: string, days: number = 1) => {
  const expiration = new Date();

  expiration.setTime(expiration.getTime() + (days * 24 * 60 * 60 * 1000));

  document.cookie = `${name}=${value}; expires=${expiration.toUTCString()}; path=/`;
};

const readCookie = (name: string): string | null => {
  const n = `${name}=`;
  const cookie = document.cookie.split(';');

  for (let i = 0; i < cookie.length; i++) {
    let c = cookie[i];

    while (c.charAt(0) === ' ')
      c = c.substring(1, c.length);

    if (c.indexOf(n) === 0)
      return c.substring(n.length, c.length);
  }

  return null;
};

const gclid = {
  current() {
    return getQueryVariable('gclid');
  },

  record() {
    const currentGclid = this.current();

    if (currentGclid) {
      // @TODO document this
      const gclsrc = getQueryVariable('gclsrc');

      if (!gclsrc || gclsrc.indexOf('aw') !== -1)
        setCookie('gclid', currentGclid, 90);
    }
  },

  get() {
    return readCookie('gclid');
  }
};

// @TODO maybe split these out into own module
const hubspot = {
  getHubspotUtk() {
    return readCookie('hubspotutk');
  },

  _hsq() {
    window._hsq = window._hsq || [];

    return window._hsq;
  },

  convertModel(model: Partial<HubspotModel>) {
    const hubspotFields: HubspotFields = [];

    Object.keys(model).forEach(fieldName => {
      const name = fieldName as keyof HubspotModel;
      const value = model[name];

      hubspotFields.push({
        name,
        value
      });
    });

    return hubspotFields;
  },

  trackDemo(data: { email: string; conversationId: number; }) {
    this._hsq().push([
      'identify',
      {
        email: data.email
      }
    ]);

    this._hsq().push([
      'trackEvent',
      {
        id: 'demo_requested',
        value: data.conversationId
      }
    ]);
  },

  trackInboxAnalysis(email: string) {
    this._hsq().push([
      'identify',
      {
        email
      }
    ]);

    this._hsq().push([
      'trackEvent',
      {
        id: 'inbox_analysis_completed'
      }
    ]);
  },

  postForm(tracking: {
    email: string;
    affiliate?: string;
    do_not_contact?: boolean;
    front_phone_number?: string;
    gclid?: string;
  }) {
    const ghostInspectorPattern = /^fronttestcompany-ghostinspector[a-zA-Z0-9]*@email.ghostinspector.com$/i;

    // We don't want ghost inspector contacts syncing into Hubspot.
    if (ghostInspectorPattern.test(tracking.email))
      return Promise.resolve();

    const hubspotModel: Partial<HubspotModel> = {
      affiliate: tracking.affiliate || getQueryVariable('affiliate') || '',
      do_not_contact: tracking.do_not_contact || false,
      email: tracking.email,
      front_phone_number: tracking.front_phone_number || '',
      hull_gclid: tracking.gclid || gclid.get() || '',
      page_url_form_: window.location.pathname,
      utm_campaign: getQueryVariable('utm_campaign') || '',
      utm_medium: getQueryVariable('utm_medium') || '',
      utm_source: getQueryVariable('utm_source') || '',
      utm_term: getQueryVariable('utm_term') || ''
    };

    const hubspotFields = this.convertModel(hubspotModel);

    /**
    * Use Hubspot Tracking Code API to submit UTM data
    * Will associate page views with hubspot cookie (hubspotutk)
    * function call ('identify') stores the data in the tracker, but the data is not actually passed to HubSpot with this call.
    */
    this._hsq().push([
      'identify',
      {
        email: tracking.email
      }
    ]);

    /**
    * The data will only be passed when tracking a pageview or an event
    * (with either the trackPageView or trackEvent functions).
    */
    this._hsq().push([
      'trackEvent',
      {
        id: 'signup_form_completed'
      }
    ]);

    const hubspotFormSubmission: HubspotFormSubmission = {
      fields: hubspotFields,
      context: {
        pageUri: window.location.href
      }
    };

    const hutk = this.getHubspotUtk();

    if (hutk)
      hubspotFormSubmission.context.hutk = hutk;

    return axios({
      url: 'https://api.hsforms.com/submissions/v3/integration/submit/4766782/1631090b-e0ac-4703-9cf7-6d0449181bfc',
      method: 'POST',
      timeout: 2000,
      data: hubspotFormSubmission
    }).catch(() => {
      // @TODO log this!
      // Silently catch the error to avoid breaking sign up flow
    });
  }
};

const sendEvent = (name: string, value?: any) => {
  const faid = getFaid();

  if (faid && window.location.href.includes('front.com'))
    return axios({
      url: `${process.env.GATSBY_FRONT_APP_HOST}/api/1/noauth/event`,
      method: 'POST',
      data: {
        name,
        value: JSON.stringify(value),
        time: Date.now()
      }
    }).catch(() => {
      // @TODO log this!
    });

  // So the return signature is consistent.
  return Promise.resolve();
};

const trackSignup = (tracking: {
  email: string;
  phone: string;
  affiliate: string;
  do_not_contact: boolean;
  teammate_id: string;
  label: string;
}) => {
  const payload = {
    ...tracking,
    gclid: gclid.get()
  };

  if (window.analytics)
    window.analytics.track('Sign-up', payload);

  if (window.dataLayer)
    window.dataLayer.push({
      ...payload,
      event: 'signup'
    });

  // Capterra conversion tracking
  // @TODO move this into Segment or GTM
  const capterra_vkey = '1742e49cb108a2e4712489bfde348311';
  const capterra_vid = '2093635';
  const capterra_prefix = `${document.location.protocol}//ct.capterra.com`;

  (() => {
    const ct = document.createElement('script'); ct.type = 'text/javascript'; ct.async = true;
    ct.src = `${capterra_prefix}/capterra_tracker.js?vid=${capterra_vid}&vkey=${capterra_vkey}`;
    // @ts-ignore
    const s = document.getElementsByTagName('script')[0]; s.parentNode.insertBefore(ct, s);
  })();
};

const trackDemoRequest = (data: { [key: string]: any }) => {
  if (window.dataLayer)
    window.dataLayer.push({
      ...data,
      event: 'demo_request'
    });

  // Capterra conversion tracking
  // @TODO move this into Segment or GTM
  const capterra_vkey = '1742e49cb108a2e4712489bfde348311';
  const capterra_vid = '2093635';
  const capterra_prefix = `${document.location.protocol}//ct.capterra.com`;

  (() => {
    const ct = document.createElement('script'); ct.type = 'text/javascript'; ct.async = true;
    ct.src = `${capterra_prefix}/capterra_tracker.js?vid=${capterra_vid}&vkey=${capterra_vkey}`;
    // @ts-ignore
    const s = document.getElementsByTagName('script')[0]; s.parentNode.insertBefore(ct, s);
  })();
};

export {
  getQueryVariable,
  generateGuid,
  getFaid,
  setFaid,
  setCookie,
  readCookie,
  gclid,
  hubspot,
  sendEvent,
  trackSignup,
  trackDemoRequest
};
